import { ReactNode } from 'react';
import { NotificationInstance } from 'antd/es/notification/interface';
import { ArgsProps } from 'antd/lib/notification';
import { TOptions } from 'i18next';
import { TFuncKey } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import { StateCreator, createStore } from 'zustand';
import { DevtoolsOptions, devtools } from 'zustand/middleware';

export type TranslatableText = [TFuncKey | TFuncKey[], TOptions] | string;

export type ToastType = 'short' | 'long' | 'critical';

export interface TToast extends Omit<ArgsProps, 'key' | 'description' | 'message' | 'type' | 'btn'> {
  description?: TranslatableText;
  message?: TranslatableText;
  type: ToastType;
  method: keyof Omit<NotificationInstance, 'destroy'>;
  key: string;
  btn?: ((toastInstance: NotificationInstance) => ReactNode) | ArgsProps['btn'];
}

export type ToastPayload = Omit<TToast, 'key'> & Partial<Pick<TToast, 'key'>>;

type ToastItem = Record<string, TToast>;

type State = {
  toasts: Record<ToastType, ToastItem>;
};

type Actions = {
  addToast: (toast: ToastPayload) => void;
  closeToasts: (type: ToastType, key?: string) => void;
};

type ToastState = State & Actions;

const createToastSlice: StateCreator<ToastState, [['zustand/devtools', never]], [], ToastState> = (set, get) => ({
  toasts: {
    short: {},
    long: {},
    critical: {},
  },
  addToast: (toast) =>
    // ? Delay toasts to avoid closing them before they are shown
    setTimeout(() => {
      const key = toast.key || uuidv4();
      set(
        {
          toasts: {
            ...get().toasts,
            [toast.type]: {
              ...get().toasts[toast.type],
              [key]: { ...toast, key },
            },
          },
        },
        false,
        'addToast',
      );
    }, 1),
  closeToasts: (type, key) => {
    const oldToasts = { ...get().toasts[type] };

    if (key) {
      delete oldToasts[key];
    }

    set(
      {
        toasts: {
          ...get().toasts,
          [type]: key ? oldToasts : {},
        },
      },
      false,
      'closeToasts',
    );
  },
});

const devtoolsOptions: DevtoolsOptions = { name: 'Toasts' };

export const toastStore = createStore<ToastState>()(devtools(createToastSlice, devtoolsOptions));

export const { addToast } = toastStore.getState();
